import { createContext, Provider, useContext } from 'react';

const NO_PROVIDER: unique symbol = Symbol('NO_PROVIDER');

export interface StrictOptionalContext<T> {
  Provider: Provider<T>;
  /** The value of the nearest provider; throws outside of it. */
  useRequiredContext: () => T;
  /** The value of the nearest provider; `undefined` outside of it. */
  useOptionalContext: () => T | undefined;
}

/**
 * A context that tells apart a missing provider from any value it provides, `undefined`
 * included: a sentinel, not `undefined`, marks the missing provider.
 */
export function createStrictOptionalContext<T>(
  hookName: string,
  providerName: string
): StrictOptionalContext<T> {
  const Context = createContext<T | typeof NO_PROVIDER>(NO_PROVIDER);
  Context.displayName = providerName;

  const useRequiredContext = (): T => {
    const value = useContext(Context);
    if (value === NO_PROVIDER) {
      throw new Error(`${hookName} must be used within ${providerName}`);
    }
    return value;
  };

  const useOptionalContext = (): T | undefined => {
    const value = useContext(Context);
    return value === NO_PROVIDER ? undefined : value;
  };

  return {
    // Providers only ever give a `T`: the sentinel is the default alone.
    Provider: Context.Provider as Provider<T>,
    useRequiredContext,
    useOptionalContext,
  };
}
